import { HttpAgent } from '@icp-sdk/core/agent'
import { formatUnits } from 'viem'
import ChainFusionClient from '../clients/ChainFusionClient'
import HyperLiquidClient from '../clients/HyperLiquidClient'
import {
  USDC_ARB_ID,
  USDC_HYPERCORE_PERP_ID,
  USDC_HYPERCORE_SPOT_ID,
  USDC_HYPEREVM_ID,
} from '../constants/tokenIds'
import { PlugToken } from '../interfaces/common'
import { SwapProvider, SwapProviderQuote } from '../interfaces/swapInterface'
import { Wallet } from '../providers/WalletProvider'
import { sendToken } from '../utils/sendToken'

const CORE_USDC_IDS = [USDC_HYPERCORE_SPOT_ID, USDC_HYPERCORE_PERP_ID]

// HyperLiquid charges a flat $1 USDC fee to withdraw from the perp account to Arbitrum.
const WITHDRAW_FEE_USD = 1

// Base for HyperCore token system addresses: first byte 0x20, remaining bytes
// zero except the spot token index encoded big-endian in the trailing bytes.
// A spotSend to this address bridges the token to its linked HyperEVM ERC-20.
const SYSTEM_ADDRESS_BASE = BigInt('0x2000000000000000000000000000000000000000')

const isCoreUsdc = (token: PlugToken) => CORE_USDC_IDS.includes(token.id)

const isHyperEvmUsdc = (token: PlugToken) => token.id === USDC_HYPEREVM_ID

const hypercoreSystemAddress = (tokenIndex: string): `0x${string}` => {
  const address = (SYSTEM_ADDRESS_BASE + BigInt(tokenIndex)).toString(16).padStart(40, '0')
  return `0x${address}`
}

// Bridges USDC between a wallet's HyperCore accounts (perp/spot) and the linked
// HyperEVM ERC-20, surfaced as a swap "provider". All conversions are 1:1 with
// no protocol fee. Direction is inferred from the from/to token pair:
//   - spot <-> perp:      usdClassTransfer (Core-internal)
//   - spot/perp -> EVM:   spotSend to the token system address (perp goes via spot first)
//   - EVM -> spot:        ERC-20 transfer to the token system address
class HyperliquidSwapClient {
  agent: HttpAgent

  constructor(agent: HttpAgent) {
    this.agent = agent
  }

  static isSupportedPair(fromToken: PlugToken, toToken: PlugToken) {
    // spot <-> perp
    if (isCoreUsdc(fromToken) && isCoreUsdc(toToken) && fromToken.id !== toToken.id) {
      return true
    }

    // Core (spot|perp) -> HyperEVM
    if (isCoreUsdc(fromToken) && isHyperEvmUsdc(toToken)) {
      return true
    }

    // HyperEVM -> Core spot (perp is reached afterwards via the spot<->perp swap)
    if (isHyperEvmUsdc(fromToken) && toToken.id === USDC_HYPERCORE_SPOT_ID) {
      return true
    }

    // perp -> USDC on Arbitrum (withdraw off HyperLiquid)
    if (fromToken.id === USDC_HYPERCORE_PERP_ID && toToken.id === USDC_ARB_ID) {
      return true
    }

    return false
  }

  getQuote(amount: bigint, fromToken: PlugToken, toToken: PlugToken): SwapProviderQuote {
    // Withdrawing to Arbitrum costs a flat $1 fee; every other conversion is 1:1.
    const feeE8s =
      toToken.id === USDC_ARB_ID ? Math.round(WITHDRAW_FEE_USD * 10 ** fromToken.decimals) : 0
    const price = Number(amount) - feeE8s

    return {
      provider: SwapProvider.HYPERLIQUID,
      price,
      priceHuman: price * 10 ** -fromToken.decimals,
    }
  }

  async swap(
    amount: bigint,
    fromToken: PlugToken,
    toToken: PlugToken,
    wallet: Wallet,
    tokens: PlugToken[]
  ) {
    if (!wallet.ethAddress) {
      throw new Error('Missing wallet address for HyperCore')
    }

    const humanAmount = formatUnits(amount, fromToken.decimals)

    // perp -> USDC on Arbitrum: withdraw off HyperLiquid to the wallet's ETH address
    if (fromToken.id === USDC_HYPERCORE_PERP_ID && toToken.id === USDC_ARB_ID) {
      await this.#execute((c) => c.prepareWithdraw(humanAmount, wallet.ethAddress!))
      return Number(amount)
    }

    // spot <-> perp: a single Core-internal transfer
    if (isCoreUsdc(fromToken) && isCoreUsdc(toToken)) {
      const toPerp = toToken.id === USDC_HYPERCORE_PERP_ID
      await this.#execute((c) => c.prepareUsdClassTransfer(humanAmount, toPerp))
      return Number(amount)
    }

    // Core (spot|perp) -> HyperEVM via spotSend to the token system address
    if (isCoreUsdc(fromToken)) {
      if (fromToken.id === USDC_HYPERCORE_PERP_ID) {
        // perp -> spot first, then bridge from spot
        await this.#execute((c) => c.prepareUsdClassTransfer(humanAmount, false))
      }

      const spotToken = this.#findSpotToken(tokens)
      const systemAddress = hypercoreSystemAddress(spotToken.additionalData.hypercore_id)

      await this.#execute((c) => c.prepareSpotSend(humanAmount, systemAddress, spotToken.address!))
      return Number(amount)
    }

    // HyperEVM -> Core spot: ERC-20 transfer to the token system address
    const spotToken = this.#findSpotToken(tokens)
    const systemAddress = hypercoreSystemAddress(spotToken.additionalData.hypercore_id)

    await sendToken(fromToken, systemAddress, amount, wallet)
    return Number(amount)
  }

  #findSpotToken(tokens: PlugToken[]) {
    const spotToken = tokens.find((token) => token.id === USDC_HYPERCORE_SPOT_ID)

    if (!spotToken?.address) {
      throw new Error('HyperCore spot USDC token not found')
    }

    return spotToken
  }

  async #execute(
    prepare: (client: HyperLiquidClient) => { action: any; digest: any; nonce: number }
  ) {
    const hyperliquidClient = new HyperLiquidClient()
    const { action, digest, nonce } = prepare(hyperliquidClient)

    const signature = await new ChainFusionClient(this.agent).signEthHash(digest)
    if (!signature) throw new Error('Unable to sign HyperCore transfer')

    return hyperliquidClient.executeExchangeAction({ action, signature, nonce })
  }
}

export default HyperliquidSwapClient
